import type { HandleMeta } from '@/lib/canvas/types'
import {
  presentStyleTransfer,
  type StyleTransferConnection,
  type StyleTransferPresentation,
} from '@genieartinc/schemas/pools/ai'

/** Picture URL carried by an upstream file or generated node. */
export function imageUrlFromFlowNode(node: { type?: string | null; data?: unknown } | null | undefined): string | undefined {
  if (!node?.data || typeof node.data !== 'object') return undefined
  const data = node.data as Record<string, unknown>
  const state = (data.state && typeof data.state === 'object' ? data.state : data) as Record<string, unknown>
  const fileMeta = state.fileMetadata as { publicUrl?: string } | undefined
  if (typeof fileMeta?.publicUrl === 'string' && fileMeta.publicUrl) return fileMeta.publicUrl
  const created = state.createdItems as Array<{ type?: string; content?: unknown }> | undefined
  if (created) {
    for (let i = created.length - 1; i >= 0; i--) {
      const item = created[i]
      if (item?.type === 'image' && typeof item.content === 'string' && item.content) return item.content
    }
  }
  const images = state.images as unknown[] | undefined
  if (images) {
    for (let i = images.length - 1; i >= 0; i--) {
      if (typeof images[i] === 'string' && images[i]) return images[i] as string
    }
  }
  return undefined
}

export function styleTransferConnections(
  nodeId: string,
  handles: readonly HandleMeta[],
  nodes: readonly { id: string; type?: string | null; data?: unknown }[],
  edges: readonly { target?: string | null; targetHandle?: string | null; source?: string | null }[],
): StyleTransferConnection[] {
  return edges.flatMap((edge) => {
    if (edge.target !== nodeId || !edge.targetHandle) return []
    if (!handles.some((handle) => handle.id === edge.targetHandle && handle.type !== 'output')) return []
    const source = nodes.find((node) => node.id === edge.source)
    const url = imageUrlFromFlowNode(source)
    return [{ handleId: edge.targetHandle, url, pending: !url }]
  })
}

export function styleTransferPresentationForNode(input: {
  subPurpose?: string
  paramNames?: readonly string[]
  handles?: readonly HandleMeta[]
  nodeId?: string
  nodes?: readonly { id: string; type?: string | null; data?: unknown }[]
  edges?: readonly { target?: string | null; targetHandle?: string | null; source?: string | null }[]
}): StyleTransferPresentation | null {
  if (input.subPurpose !== 'style-transfer' || !input.nodeId) return null
  const handles = input.handles ?? []
  return presentStyleTransfer({
    subPurpose: input.subPurpose,
    paramNames: input.paramNames,
    handles: handles.map((handle) => ({
      id: handle.id,
      type: handle.type,
      dataType: handle.dataType,
      parameterName: handle.metadata?.parameterName,
      ordered: typeof handle.position === 'object' && handle.position && 'ordered' in handle.position ? handle.position.ordered : undefined,
    })),
    connections: styleTransferConnections(input.nodeId, handles, input.nodes ?? [], input.edges ?? []),
  })
}
